import { useEffect } from "react";

/**
 * useZoomScale
 *
 * Detects the effective browser zoom level by comparing window.innerWidth
 * against the design's base width (1536px @ 100% zoom).
 *
 * At higher zoom levels (110%, 125%, 150%) the browser reports a smaller
 * innerWidth. We compensate by scaling #root down so the design always
 * renders at its intended size, keeping layout pixel-perfect at all zoom levels.
 *
 * Base width: 1536px (design reference at 100% zoom, 1536×729 resolution)
 *
 * Scale is capped at 1.0 — we never scale UP, only DOWN.
 */

const BASE_WIDTH = 1536;

// Matches standard mobile/tablet breakpoint (992px)
// Any viewport at or below this width is treated as mobile — NO scaling applied.
const MOBILE_BREAKPOINT = 992;

let lastAppliedScale = null;
let lastAppliedHeight = null; // Add this line
let lastAppliedWidth = null;  // Add this line

function resetScale() {
  if (lastAppliedScale === 1) return;
  lastAppliedScale = 1;
  lastAppliedHeight = null; // Add this
  lastAppliedWidth = null;  // Add this
  
  const root = document.getElementById("root");
  if (!root) return;
  root.style.transform = "";
  root.style.transformOrigin = "";
  root.style.width = "";
  root.style.height = "";
  root.style.minWidth = "";
  root.style.minHeight = "";
  document.documentElement.style.setProperty("--app-full-height", "100vh");
  document.documentElement.style.setProperty("--app-full-width", "100vw");
  document.documentElement.style.setProperty("--zoom-scale", "1");
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;

  // 0. Physical phone detection (hardware screen min dimension <= 500px, e.g. iPhone 14 Pro Max is 430px)
  const minHardwareDim = Math.min(
    window.screen?.width || 9999,
    window.screen?.height || 9999
  );
  if (minHardwareDim <= 500) {
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("is-phone-device");
    }
    return true;
  }

  // 1. Standard mobile / small-tablet breakpoint (covers standard mobile, tablet portrait, and iOS 980px desktop view)
  if (window.innerWidth <= MOBILE_BREAKPOINT) return true;

  // 2. Mobile landscape: constrained vertical height on phones
  if (window.innerHeight <= 550 && window.innerWidth <= 1024) return true;

  // 3. Media query check for landscape mobile
  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(orientation: landscape) and (max-height: 550px)").matches
  ) {
    return true;
  }

  // 4. Touch device with phone-sized or tablet dimension
  const minDim = Math.min(
    window.screen?.width || window.innerWidth,
    window.screen?.height || window.innerHeight
  );
  const isTouch =
    ("ontouchstart" in window || navigator.maxTouchPoints > 0) &&
    (typeof window.matchMedia === "function"
      ? window.matchMedia("(pointer: coarse)").matches
      : true);

  if (isTouch && (minDim <= 600 || window.innerWidth <= 1024)) return true;

  // 5. Mobile User-Agent check (including Chrome on iOS: CriOS)
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|CriOS/i.test(
      navigator.userAgent || ""
    ) &&
    (window.innerWidth <= 1024 || window.innerHeight <= 600)
  ) {
    return true;
  }

  // 6. iOS WebKit in desktop mode sends Mac user-agent with touch points > 1
  if (
    navigator.maxTouchPoints > 1 &&
    /Macintosh/i.test(navigator.userAgent || "") &&
    (window.innerWidth <= 1024 || window.innerHeight <= 600)
  ) {
    return true;
  }

  return false;
}

function applyZoomScale() {
  const root = document.getElementById("root");
  if (!root) return;

  // Native fullscreen needs an untransformed root. A transformed #root
  // changes how position: fixed headers are positioned and can clip them.
  if (document.fullscreenElement) {
    resetScale();
    // Undo any previous document-level scrolling. The outcome cards have
    // their own scroll container and must not move the application shell.
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    return;
  }


  // ── GRAPH FULLSCREEN / EXPAND GUARD ──────────────────────────────────────
  // When expand / fullscreen graph is active, #root MUST NOT have transform: scale().
  // CSS transform on #root breaks position: fixed of .graph-toggle, causing it to
  // be confined within #root, leaking headers/footers, and clipping the chart.
  if (typeof document !== "undefined" && document.querySelector(".graph-toggle")) {
    resetScale();
    return;
  }

  // ── MOBILE GUARD ──────────────────────────────────────────────────────────
  // If we're on a mobile / small-screen / mobile-landscape viewport, skip scaling entirely.
  // Let the existing MobileAdaptive.css & media queries handle those sizes.
  if (isMobileViewport()) {
    resetScale();
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("is-mobile-device");
    }
    return;
  } else {
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("is-mobile-device");
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  let scale = Math.min(1, window.innerWidth / BASE_WIDTH);

  // Deadband around 100% zoom: if scale >= 0.975 (e.g. within 38px of 1536 due to scrollbars),
  // treat it strictly as 100% zoom (1.0). This prevents scrollbars toggling from triggering
  // a scale-down, which hides scrollbar, triggering scale-up, causing an infinite resize loop.
  if (scale >= 0.975) {
    scale = 1;
  }

  if (scale < 1) {
    // Check if scale is unchanged AND height/width are unchanged
    if (
      lastAppliedScale !== null && 
      Math.abs(scale - lastAppliedScale) < 0.005 &&
      lastAppliedHeight === window.innerHeight &&
      lastAppliedWidth === window.innerWidth
    ) {
      return; // Only skip recalculation if NOTHING changed
    }
    
    // Update the trackers
    lastAppliedScale = scale;
    lastAppliedHeight = window.innerHeight;
    lastAppliedWidth = window.innerWidth;

    const inverseScale = 1 / scale;
    const fullWidth = Math.round(window.innerWidth * inverseScale);
    const fullHeight = Math.round(window.innerHeight * inverseScale);

    // Scale down to compensate for zoomed viewport
    root.style.transform = `scale(${scale})`;
    root.style.transformOrigin = "top left";
    // Set explicit pixel dimensions so #root fills logical design space perfectly
    root.style.width = `${fullWidth}px`;
    root.style.height = `${fullHeight}px`;
    root.style.minWidth = `${fullWidth}px`;
    root.style.minHeight = `${fullHeight}px`;

    // Inject compensated dimensions for CSS
    document.documentElement.style.setProperty("--app-full-height", `${fullHeight}px`);
    document.documentElement.style.setProperty("--app-full-width", `${fullWidth}px`);
    document.documentElement.style.setProperty("--zoom-scale", `${scale}`);
  } else {
    // 100% zoom or larger viewport — reset all scaling
    resetScale();
  }
}

export function useZoomScale() {
  useEffect(() => {
    // Apply immediately on mount
    applyZoomScale();

    const handleOrientation = () => {
      applyZoomScale();
      setTimeout(applyZoomScale, 150);
    };

    // Re-apply whenever viewport changes (zoom, resize, window snap, rotation)
    //window.addEventListener("resize", applyZoomScale);
	let resizeTicking = false;
	const optimizedResize = () => {
	  if (!resizeTicking) {
		window.requestAnimationFrame(() => {
		  applyZoomScale();
		  resizeTicking = false;
		});
		resizeTicking = true;
	  }
	};

	window.addEventListener("resize", optimizedResize);
    window.addEventListener("orientationchange", handleOrientation);
    // requestFullscreen() may change layout without firing resize.
    document.addEventListener("fullscreenchange", applyZoomScale);
    return () => {
      window.removeEventListener("resize", optimizedResize);
      window.removeEventListener("orientationchange", handleOrientation);
		document.removeEventListener("fullscreenchange", applyZoomScale);
    };
  }, []);
}

/**
 * applyZoomScaleDirect
 * For use in class components (componentDidMount / componentWillUnmount).
 * Returns the cleanup function.
 */
export function applyZoomScaleDirect() {
  applyZoomScale();
  const handleOrientation = () => {
    applyZoomScale();
    setTimeout(applyZoomScale, 150);
  };
	let resizeTicking = false;
	const optimizedResize = () => {
	  if (!resizeTicking) {
		window.requestAnimationFrame(() => {
		  applyZoomScale();
		  resizeTicking = false;
		});
		resizeTicking = true;
	  }
	};

	window.addEventListener("resize", optimizedResize);
  //window.addEventListener("resize", applyZoomScale);
    window.addEventListener("orientationchange", handleOrientation);
	document.addEventListener("fullscreenchange", applyZoomScale);

  return function cleanup() {
    //window.removeEventListener("resize", applyZoomScale);
	//let resizeTicking = false;
	document.removeEventListener("fullscreenchange", applyZoomScale);
	window.removeEventListener("resize", optimizedResize);
    window.removeEventListener("orientationchange", handleOrientation);
    // Reset root styles on unmount
    const root = document.getElementById("root");
    if (root) {
      root.style.transform = "";
      root.style.transformOrigin = "";
      root.style.width = "";
      root.style.height = "";
      root.style.minWidth = "";
      root.style.minHeight = "";
    }
  };
}
